import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { PLANS, estMeetings } from '../../monetization/plans';
import { getMySubscription } from '@/services/api/monetizationApi';
import { posthogCapture } from '@/config/posthog';

/**
 * PaymentSuccessPage — where a Stripe Payment Link sends the buyer after paying.
 *
 * COPIED FROM glowtest_FE's pattern on the PO's instruction (2026-09-20): glowtest routes `/payment-success`
 * to `RegistrationResult`, which reads WHICH PLAN from the URL (`?pl=lite|credits|…`) and deliberately
 * carries NO price and NO credit count — its own comment: "NO prices - actual values tracked server-side
 * via BE". That rule is the part worth copying, and it is why this page takes `pl` and nothing else.
 *
 * ⚠️ THE ONE THING NOT COPIED, and the reason is the whole design: a value in the URL is a value the
 * visitor can type. `?credits=999999` would render a million credits and mean nothing — it would be the
 * product's own numbers telling a lie on the one page whose entire job is to be believed (FTUE W1 "prove
 * it's real", W6 "no overpromise"). So this page states what we can know from the URL (which plan was
 * bought) and then READS THE REAL FIGURE from the account, which is written by the webhook and is the
 * record. It never renders a balance it has not read, and it never invents a zero.
 *
 * ── THE HEADLINE WAS THE ONE CLAIM LEFT UNREAD (D-123, fixed 2026-10-05) ────────────────────────────
 * The figure obeyed the rule above; the headline did not. `plan` was taken from `?pl=`, so anyone could
 * type `?pl=scale` and be told "Your Scale plan is on." — an unverified claim about the ACCOUNT, one
 * paragraph above a number that refuses to invent. The account already answers this:
 * `/my_subscription` returns `tier`, the plan the grant wrote (it was added for exactly this class of
 * bug). So a plan is now named ONLY when the account confirms it, and the tier is POLLED with the balance
 * because the same race applies — the webhook writes it, so a read can arrive before the write. While the
 * account and the URL disagree, the page says the one thing it always knows — "Payment received." —
 * rather than naming a plan nobody bought. Residual limit, recorded: for the one-off credits rail there is
 * no account field that names THIS purchase, so "Your credits are in." is shown only once the wallet has
 * genuinely been read; a passer-by who is not signed in gets the neutral headline, not a claim.
 *
 * The plan figures below are DERIVED from the same stone the plans and the blog use (`plans.ts`), so this
 * page cannot advertise a reachout count or a meeting estimate that the pricing disagrees with.
 *
 * ── WHY THIS PAGE WAS REBUILT (D-115, PO 2026-09-21) ────────────────────────────────────────────────
 * The PO paid, was sent to a dead URL, then found this page and said: *"i dont see anything here
 * neither!")* — and pointed at the reference: *"we had a great animation there. positive reward here is
 * important to FTUE"*. He was right, and the gap against `glowtest_FE/src/pages/RegistrationResult` was
 * exactly this:
 *
 *   |                              | glowtest_FE                    | this page was           |
 *   | the reward                    | a 12rem green check            | a line of teal caps     |
 *   | the headline                  | "Credits purchased successfully"|"your credits are landing"|
 *   | the next step                 | one big "Continue your journey" | a small text link       |
 *   | what it says he now HAS       | "You have enough credits!" (a CLAIM, read from nothing) | nothing |
 *
 * The honest version of that reward is not to claim: it is to READ. This page now shows the mark, a
 * headline that states what happened, and then **the balance we actually read from his wallet** — the
 * same figure his account shows. That is FTUE W1 ("prove it's real") and implication 10 ("the output is
 * delivered, not described") while keeping W6 (no overpromise): glowtest's "you have enough credits!"
 * is a claim about a state it never read, and it is the one line of its version that does not transfer.
 *
 * THE REWARD IS THE REAL NUMBER, NOT AN ANIMATION. The motion below is one arrival (the mark settling,
 * the figure appearing) — no confetti, no streak, no trophy (FTUE W11 positivity law + the no-gamification
 * rail). A purchase is the one moment a first-time user is meant to feel something; it must be true.
 */

/** The three honest states of the wallet read (never collapsed — a 0 for a purchase in flight is "not
 * yet", which is a different thing from "nothing", and printing it as the reward would be a lie by
 * presentation). */
type WalletRead =
  | { state: 'reading' }
  | { state: 'read'; credits: number; perSend: number | null }
  | { state: 'settling' }
  | { state: 'unread' };

/** How long to keep watching the wallet for the webhook's write before saying so. */
const READ_ATTEMPTS = 20;
const READ_INTERVAL_MS = 1500;

const PLAN_BY_KEY: Record<string, 'starter' | 'growth' | 'scale'> = {
  starter: 'starter',
  growth: 'growth',
  scale: 'scale',
};

export const PaymentSuccessPage = () => {
  // The URL says WHICH PLAN. It never says how much — see the note above.
  const params = new URLSearchParams(window.location.search);
  const pl = (params.get('pl') || '').toLowerCase();

  const plan = PLANS.find((p) => p.key === PLAN_BY_KEY[pl]);
  const isCredits = pl === 'credits' || pl === 'credits-topup';

  // ── P134 T1343 — THE POST-PAYMENT ENTRY THE WIZARD'S OWN COMMENT NAMES ────────────────────────
  //
  // `Wizard_Domain_Provisioning.tsx:12` says it *"can be mounted from any surface (entity page,
  // post-payment entry)"*. Measured 2026-10-05: only the FIRST half was true. The wizard was mounted
  // from the entity page and from the Integrations card, and **no post-payment entry existed** — this
  // page's only action was `href="/"`, which renders the public `LandingPage` (`CRM.tsx:370`), so a
  // buyer who had just paid for the setup was sent to the marketing site.
  //
  // WHY THE ENTRY IS A LINK AND NOT A SECOND MOUNT. The wizard needs the entity's own name, location
  // and links (GEO drives the domain suggestions, D-052), and this page has no entity — it is reached
  // from a Stripe Payment Link, which carries a plan and nothing else. A second mount would have to
  // invent the entity or ask for it, and asking a person who has just paid to re-identify their
  // company is the toil the journey exists to remove. So the entry LEADS to the one home the journey
  // has (the Integrations surface, `@P276 T0141`), where the entity is already selected.
  //
  // The rail is named by the URL, exactly as the plan is, and it is deliberately NOT a claim about
  // the account: it decides where the button points, never what the page says was bought.
  const isSetup = pl === 'setup' || pl === 'sending' || pl === 'domain';

  // THE CONVERSION (project091 T0108). Landing here IS the paid step: Stripe sends the buyer to this
  // route both from the Payment Links and from our own Checkout Sessions. The page never trusts the URL
  // for an AMOUNT (see the note above), so the event carries only WHAT was bought.
  //
  // HONEST LIMIT, recorded rather than hidden: a buyer who closes the tab at Stripe never lands here and
  // is not counted. This event says "a buyer came back from paying"; the webhook stays the authority on
  // whether a payment happened.
  useEffect(() => {
    posthogCapture('payment_success', { plan: isCredits ? 'credits' : pl || 'unknown' });
  }, [isCredits, pl]);

  /**
   * READ THE THING HE ACTUALLY BOUGHT. `getMySubscription()` is the SAME call the credits centre and the
   * settings page read his balance with, so this page cannot show a figure the rest of the product
   * disagrees with — one endpoint, one number, one story.
   *
   * IT POLLS, AND THAT IS THE HONEST PART. Stripe redirects the browser the instant the payment is taken;
   * our own webhook writes the credits a moment later. So the first read can legitimately arrive before
   * the write. Rather than print a wrong figure or a demoralising 0, the page watches for it and shows the
   * number the moment it is there. The key names are the MEASURED ones (`credit_balance`,
   * `per_send_credit_cost` — see the interface's own note about the glowtest `current_credits` port bug); a
   * wrong key would be a type error now, not a silently missing number.
   */
  const [wallet, setWallet] = useState<WalletRead>({ state: 'reading' });
  // THE ACCOUNT'S OWN PLAN (D-123). The URL says which link was clicked; this says which plan the account
  // actually holds, written by the webhook. Polled with the balance for the same reason.
  const [accountTier, setAccountTier] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    let timer: number | undefined;
    // DID THE API EVER ANSWER FOR THIS PERSON? That is a different question from "is his balance
    // above zero", and the two were being answered as one — which made `unread` DEAD CODE and put a
    // false promise on the screen (measured 2026-09-26 on raisy.team, signed out: "Your payment is
    // confirmed. Your credits are still being added — that takes a moment.", repeated forever).
    // Nothing was being added: we had simply never read his account, and the page said the most
    // reassuring thing it could instead of the true one. A confirmation page may not do that.
    let answered = false;

    const readOnce = async (): Promise<boolean> => {
      try {
        const res = await getMySubscription();
        const sub = res?.data;
        if (typeof sub?.credit_balance === 'number') {
          answered = true;
          if (!cancelled && typeof sub.tier === 'string') setAccountTier(sub.tier);
          if (sub.credit_balance > 0) {
            if (!cancelled) {
              setWallet({
                state: 'read',
                credits: sub.credit_balance,
                perSend: typeof sub.per_send_credit_cost === 'number' ? sub.per_send_credit_cost : null,
              });
            }
            return true;
          }
          // A real account, a real answer, and a zero: the purchase has not landed YET. Keep watching —
          // this is the one case where "still being added" is true, and it is decided below.
        }
        return false;
      } catch {
        // Not signed in, or the call failed. Still not an answer, so `answered` stays false.
        return false;
      }
    };

    const tick = async () => {
      const done = await readOnce();
      if (cancelled || done) return;
      if (++attempts >= READ_ATTEMPTS) {
        // We watched and did not see the credits. WHICH of the two things happened decides what we are
        // allowed to say: the account answered and the write has not landed (`settling` — watch it
        // arrive), or the account never answered at all (`unread` — the record is the account, go and
        // look there). Saying the first when the second is true is a claim about our own system that we
        // have no basis for.
        if (!cancelled) {
          setWallet((w) => (w.state === 'reading' ? { state: answered ? 'settling' : 'unread' } : w));
        }
        return;
      }
      timer = window.setTimeout(tick, READ_INTERVAL_MS);
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  /** A confirmation page is private: it carries a payment and a balance, so it is never indexed. */
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  // ── WHAT THE PAGE IS ALLOWED TO SAY (D-123) ──────────────────────────────────────────────────────
  // The account is the authority on the plan; the URL only says which link was clicked. A plan is named
  // ONLY when the account confirms it. Anyone can type `?pl=scale`; nobody can make the account say it.
  const accountPlan = accountTier && accountTier !== 'free' ? accountTier : null;
  const confirmedPlan = plan && accountPlan === plan.key ? plan : undefined;
  const creditsConfirmed = isCredits && wallet.state === 'read' && wallet.credits > 0;

  const headline = confirmedPlan
    ? `Your ${confirmedPlan.name} plan is on.`
    : creditsConfirmed
      ? 'Your credits are in.'
      : 'Payment received.';

  // The price paragraph is the same claim in numbers, so it is gated by the same verdict.
  const bought = confirmedPlan
    ? `That is $${confirmedPlan.monthlyPriceUsd} a month, buying about ${confirmedPlan.reachoutsPerMonth.toLocaleString('en-IE')} investor reachouts — which we estimate at around ${estMeetings(confirmedPlan)} investor meetings a month, about $${(confirmedPlan.monthlyPriceUsd / estMeetings(confirmedPlan)).toFixed(2)} per investor meeting.`
    : creditsConfirmed
      ? 'Credits are the platform\u2019s currency: 1 USD = 1,000 credits, and they spend on anything we sell. What you paid is what lands.'
      : null;

  return (
    <div className="min-h-screen bg-white px-4 py-16 dark:bg-slate-900">
      <main className="mx-auto max-w-xl text-center">
        {/* THE REWARD MOMENT — the shape glowtest_FE had and this page did not. It arrives once
            (see `.payment-reward-mark` in index.css) and then holds still. */}
        <span className="payment-reward-mark mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-500/10">
          <Check className="h-14 w-14 text-emerald-600 dark:text-emerald-400" strokeWidth={3} aria-hidden />
        </span>

        <p className="mt-6 text-sm font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
          Payment received
        </p>
        <h1 className="mt-2 text-3xl font-bold text-slate-900 dark:text-white md:text-4xl">{headline}</h1>

        {bought ? (
          <p className="mt-4 text-slate-700 dark:text-slate-300">{bought}</p>
        ) : null}

        {/* WHAT HE NOW HAS — READ, NEVER CLAIMED. */}
        {wallet.state === 'read' && wallet.credits > 0 ? (
          <div className="payment-reward-figure mt-8 rounded-xl border border-slate-200 bg-slate-50 px-6 py-5 dark:border-slate-700 dark:bg-slate-800/60">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              In your account now
            </p>
            <p className="mt-1 text-4xl font-bold text-slate-900 dark:text-white">
              {wallet.credits.toLocaleString('en-IE')}
              <span className="ml-2 text-lg font-semibold text-slate-600 dark:text-slate-300">credits</span>
            </p>
            {wallet.perSend ? (
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                That is <strong>{wallet.perSend.toLocaleString('en-IE')} credits an email</strong> at your
                current rate.
              </p>
            ) : null}
          </div>
        ) : wallet.state === 'reading' ? (
          <p className="mt-8 text-sm text-slate-500 dark:text-slate-400">Reading your credit balance…</p>
        ) : wallet.state === 'settling' ? (
          <p className="mt-8 text-sm text-slate-600 dark:text-slate-400">
            Your payment is confirmed. Your credits are still being added — that takes a moment. They
            land on your account, so you can watch them arrive there.
          </p>
        ) : (
          // `unread` — WE NEVER GOT AN ANSWER, which is not the same as a slow answer, and must not be
          // dressed up as one. He may simply not be signed in on this browser; either way the account is
          // where the number is written, so that is where he is sent.
          <p className="mt-8 text-sm text-slate-600 dark:text-slate-400">
            Your account is the record: it shows the balance the moment it is written. Sign in and open
            it, and you will see the figure there.
          </p>
        )}

        <a
          href={isSetup ? '/integrations?provider_category=mail' : '/'}
          className="mt-8 inline-block rounded-lg bg-teal-600 px-6 py-3 text-base font-semibold text-white transition hover:bg-teal-700"
        >
          {isSetup ? 'Set up your sending address' : 'Continue to your account'}
        </a>

        <p className="mt-6 text-xs text-slate-500 dark:text-slate-400">
          <strong>Where the record is.</strong> This page tells you Stripe took the payment; the balance
          and plan above are read from your account, which is the record. If your account does not show
          what you bought within a few minutes, reply to your receipt and we will look at it with you.
        </p>
      </main>
    </div>
  );
};
